import { createGlobalStyle } from 'styled-components';
import { QUERIES } from '../constants/mediaQueries';

const GlobalStyles = createGlobalStyle`
    /* =========================
       1. CSS Variables
    ========================= */
    :root {
        /* Colors */
        --color-neutral-white: #FFFFFF;
        --color-neutral-lightest: #F2F2F2;
        --color-neutral-lighter: #DADAD9;
        --color-neutral-light: #B5B5B4;
        --color-neutral: #848482;
        --color-neutral-dark: #535250;
        --color-neutral-darker: #22211E;
        --color-neutral-darkest: #0A0905;
        --color-neutral-ink: #0C0C0C;
        --color-neutral-graphite: #2B2B2B;
        --color-cta: var(--color-neutral-darkest);
        --color-cta-hover: var(--color-neutral-darker);
        --color-cta-active: var(--color-neutral-ink);
        --color-primary: var(--color-neutral-darkest);
        --color-text-primary: var(--color-neutral-darkest);
        --color-border: var(--color-neutral-dark);

        /* Social Media Colors */
        --color-whatsapp: #25D366;
        --color-instagram: #E4405F;

        /* Opacity */
        --opacity-color-white-01: rgba(255, 255, 255, 0.01);
        --opacity-color-white-05: rgba(255,255,255,0.05);
        --opacity-color-white-08: rgba(255, 255, 255, 0.08);
        --opacity-color-white-10: rgba(255, 255, 255, 0.1);
        --opacity-color-white-15: rgba(255, 255, 255, 0.15);
        --opacity-color-white-20: rgba(255,255,255,0.2);
        --opacity-color-white-25: rgba(255, 255, 255, 0.25);
        --opacity-color-white-60: rgba(255, 255, 255, 0.6);
        --opacity-color-neutral-darkest-08: rgba(10, 9, 5, 0.08);
        --opacity-color-neutral-darkest-15: rgba(10, 9, 5, 0.15);
        --opacity-color-neutral-darkest-58: rgba(10, 9, 5, 0.58);
        --opacity-color-neutral-darkest-78: rgba(10, 9, 5, 0.78);
        --opacity-color-neutral-darkest-50: rgba(10, 9, 5, 0.5);
        --opacity-color-black-15: rgba(0, 0, 0, 0.15);
        --opacity-color-black-35: rgba(0, 0, 0, 0.35);
        --opacity-color-black-60: rgba(0, 0, 0, 0.6);
        --opacity-color-black-10: rgba(0, 0, 0, 0.1);
        --opacity-color-black-09: rgba(0, 0, 0, 0.09);
        --opacity-color-black-04: rgba(0, 0, 0, 0.04);
        --opacity-color-cta-30: rgba(10, 9, 5, 0.12);
        --opacity-color-cta-42: rgba(10, 9, 5, 0.2);

        /* Typography */
        --font-family: "Inter", Georgia, 'Times New Roman', serif;
        --font-family-emphasis: "Suwannaphum", Georgia, 'Times New Roman', serif;
        
        /* Font Weights */
        --font-weight-light: 300;
        --font-weight-regular: 400;
        --font-weight-medium: 500;
        --font-weight-semibold: 600;
        --font-weight-bold: 700;
        --font-weight-heavy: 800;

        /* Heading Sizes (Desktop) */
        --heading1-size: 4rem;
        --heading1-line: 1.2;
        --heading2-size: 3rem;
        --heading2-line: 1.2;
        --heading3-size: 2.5rem;
        --heading3-line: 1.2;
        --heading4-size: 2rem;
        --heading4-line: 1.3;
        --heading5-size: 1.5rem;
        --heading5-line: 1.4;
        --heading6-size: 1.25rem;
        --heading6-line: 1.4;
        --tagline-size: 1.25rem;
        --tagline-line: 1.5;

        /* Body Sizes (Desktop) */
        --body-large-size: 1.375rem;
        --body-base-size: 1rem;
        --body-small-size: 0.875rem;
        --body-tiny-size: 0.75rem;
        --body-line: 1.5;

        /* Heading Sizes (Tablet) */
        --heading1-size-tablet: 3.25rem;
        --heading2-size-tablet: 2.5rem;
        --heading3-size-tablet: 2rem;
        --heading4-size-tablet: 1.75rem;
        --heading5-size-tablet: 1.375rem;
        --heading6-size-tablet: 1.125rem;
        --tagline-size-tablet: 1.125rem;

        /* Body Sizes (Tablet) */
        --body-large-size-tablet: 1.25rem;
        --body-base-size-tablet: 0.9375rem;
        --body-small-size-tablet: 0.8125rem;
        --body-tiny-size-tablet: 0.75rem;

        /* Heading Sizes (Mobile) */
        --heading1-size-mobile: 2.5rem;
        --heading2-size-mobile: 2.25rem;
        --heading3-size-mobile: 1.75rem;
        --heading4-size-mobile: 1.375rem;
        --heading5-size-mobile: 1.125rem;
        --heading6-size-mobile: 1rem;
        --tagline-size-mobile: 1rem;

        /* Body Sizes (Mobile) */
        --body-large-size-mobile: 1.125rem;
        --body-base-size-mobile: 0.875rem;
        --body-small-size-mobile: 0.75rem;
        --body-tiny-size-mobile: 0.625rem;

        /* Spacing */
        --spacing-xsmall: 0.3rem;
        --spacing-small: 0.5rem;
        --spacing-base: 1rem;
        --spacing-medium: 1.25rem;
        --spacing-large: 1.5rem;
        --spacing-xlarge: 2rem;

        /* Containers */
        --container-large: 1280px;
        --container-medium: 1024px;
        --container-small: 768px;

        /* Max Widths */
        --max-width-xxlarge: 1280px;
        --max-width-xlarge: 1024px;
        --max-width-large: 768px;
        --max-width-medium: 560px;
        --max-width-small: 480px;
        --max-width-xsmall: 400px;
        --max-width-xxsmall: 320px;
        --max-width-content: 32rem;
        --max-width-content-wide: 34rem;
        --max-width-content-cta: 38rem;
        --max-width-content-intro: 42rem;
        --max-width-center-intro: 760px;

        /* Page Padding (Desktop/Mobile) */
        --padding-global: 64px;
        --padding-global-mobile: 20px;

        /* Section Padding (Desktop/Mobile) */
        --padding-section-large: 112px;
        --padding-section-large-mobile: 64px;
        --padding-section-medium: 80px;
        --padding-section-medium-mobile: 48px;
        --padding-section-small: 48px;
        --padding-section-small-mobile: 32px;

        /* Legacy Containers (keeping for compatibility) */
        
        /* Layout */
        --height-text-area: 150px;
        --height-loading-indicator-min: 200px;
        --height-map-panel: 22rem;
        --height-map-panel-tablet: 26rem;
        --height-header: 100px;
        --height-footer: 4rem;
        --height-logo: 60px;
        --max-height-accordion: 500px;
        --width-icon-button: 40px;
        --width-icon-button-offset: -20px;
        --width-scrollbar: 10px;
        --width-section: 80vw;
        --width-logo: 80px;
        --width-logo-small: 50px;

        /* Button Specific Styles */
        --button-padding-v: 6px;
        --button-padding-h: 12px;
        --button-padding-icon: 0.5rem;
        --button-gap-standard: 8px;
        --button-gap-icon: 8px;
        --button-icon-only-size: 2.5rem;
        --button-icon-size: 1.25rem;
        --button-active-indicator-height: 3px;
        --button-focus-outline-width: 2px;
        --button-focus-outline-offset: 2px;
        --button-gap-burger: 0.375rem;
        --button-size-burger: 3rem;
        --button-size-burger-line-width: 1.5rem;
        --button-size-burger-line-height: 0.125rem;
        --button-radius-burger-line: 0.0625rem;

        /* UI Component Sizes (px-based) */
        --ui-padding-small: 4px;
        --ui-padding-medium: 8px;
        --ui-padding-large: 12px;
        --ui-tag-padding-v: 4px;
        --ui-tag-padding-h: 10px;
        --ui-toggle-size: 40px;
        --ui-toggle-height: 24px;
        --ui-toggle-dot-size: 16px;
        --ui-tag-font-size: 0.875rem;
        --ui-select-padding-right: 36px;
        --ui-slider-position-x: 18px;
        --ui-slider-position-y: 12px;
        --ui-select-arrow-size: 6px;

        /* Layout Helpers */
        --layout-footer-top-gap: 4vw;

        /* Animations & Transitions */
        --hover-scale: scale(1.05);
        --hover-translate-x: translateX(-3px);
        --hover-translate-y: translateY(-3px);
        --shadow-xxsmall: 0px 1px 2px rgba(0, 0, 0, 0.05);
        --shadow-xsmall: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
        --shadow-small: 0px 4px 8px -2px rgba(0, 0, 0, 0.1), 0px 2px 4px -2px rgba(0, 0, 0, 0.06);
        --shadow-medium: 0px 12px 16px -4px rgba(0, 0, 0, 0.08), 0px 4px 6px -2px rgba(0, 0, 0, 0.03);
        --shadow-large: 0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03);
        --shadow-xlarge: 0px 24px 48px -12px rgba(0, 0, 0, 0.18);
        --shadow-xxlarge: 0px 32px 64px -12px rgba(0, 0, 0, 0.14);

        --transition-all-ease: all 0.3s ease;
        --transition-accordion: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        --hover-scale-button: scale(1.03);
        --hover-scale-button-icon: scale(1.08);
        
        /* Effects & Decorations */
        --radius-xxsmall: 2px;
        --radius-xsmall: 4px;
        --radius-small: 6px;
        --radius-base: 8px;
        --radius-medium: 12px;
        --radius-large: 24px;
        --radius-xlarge: 32px;
        --radius-xxlarge: 48px;
        --radius-card: var(--radius-base);
        --radius-circle: 50%;
        --radius-button: 6px;
        --radius-input: 0px;
        --radius-tag: 4px;
        --radius-toggle: 500px;
        --border-width: 1px;
        --height-control: 40px;
        --width-control: 560px;
        --height-button-default: 36px;
        --height-slider-arrow: 48px;
        --width-slider-arrow: 48px;
        --border-transparent: var(--border-width) solid transparent;
        --border-default: none;
        --border-accent: none;
        --shadow-default: 0 0.5rem 1rem var(--color-neutral-light);

        /*
        =========================
        Scheme 1: light
        - Clean bright theme with white background and dark text
        - Used for default light theme
        =========================
        */
        --scheme1-text: var(--color-neutral-darkest);
        --scheme1-background: var(--color-neutral-white);
        --scheme1-accent: var(--color-neutral-darker);
        --scheme1-foreground: var(--color-neutral-white);
        --scheme1-border-default: var(--border-transparent);
        --scheme1-border-accent: var(--border-transparent);
        --scheme1-shadow-default: 0 0.25rem 0.75rem var(--opacity-color-neutral-darkest-08);
        --scheme1-hover-text: var(--color-neutral-white);
        --scheme1-hover-background: var(--color-neutral-darker);
        --scheme1-hover-border: var(--border-transparent);
        --scheme1-cta-text: var(--color-neutral-darkest);
        --scheme1-cta-background: var(--color-neutral-white);
        --scheme1-cta-border: var(--border-width) solid var(--color-neutral-darkest);
        --scheme1-cta-hover-text: var(--color-neutral-white);
        --scheme1-cta-hover-background: var(--color-neutral-darkest);
        --scheme1-cta-hover-border: var(--border-width) solid var(--color-neutral-darkest);
        --scheme1-cta-active-text: var(--color-neutral-white);
        --scheme1-cta-active-background: var(--color-neutral-ink);
        --scheme1-cta-active-border: var(--border-width) solid var(--color-neutral-ink);
        --scheme1-cta-shadow: none;
        --scheme1-cta-hover-shadow: none;
        --scheme1-focus-outline: var(--color-neutral-darker);
        --scheme1-active-text: var(--color-neutral-darkest);
        --scheme1-active-background: var(--color-neutral-white);
        --scheme1-active-border: var(--border-width) solid var(--color-neutral-darkest);
        --scheme1-input-background: transparent;
        --scheme1-input-border: var(--border-width) solid var(--opacity-color-neutral-darkest-15);
        --scheme1-input-color: var(--color-neutral-darkest);
        --scheme1-input-placeholder: var(--opacity-color-neutral-darkest-50);
        --scheme1-input-focus-border: var(--color-neutral-darker);
        --scheme1-input-focus-shadow: 0 0 0 3px var(--opacity-color-neutral-darkest-15);
        --scheme1-input-autofill-background: var(--color-neutral-white);
        --scheme1-input-autofill-color: var(--color-neutral-darkest);
        
        /*
        =========================
        Scheme 2: dark
        - Dark background with white text and accents
        - Used for default dark theme
        =========================
        */
        --scheme2-text: var(--color-neutral-white);
        --scheme2-background: var(--color-neutral-darkest);
        --scheme2-accent: var(--color-neutral-white);
        --scheme2-foreground: var(--color-neutral-darkest);
        --scheme2-border-default: var(--border-transparent);
        --scheme2-border-accent: var(--border-transparent);
        --scheme2-shadow-default: 0 0.25rem 0.75rem var(--opacity-color-black-35);
        --scheme2-hover-text: var(--color-neutral-darkest);
        --scheme2-hover-background: var(--color-neutral-white);
        --scheme2-hover-border: var(--border-transparent);
        --scheme2-cta-text: var(--color-neutral-white);
        --scheme2-cta-background: transparent;
        --scheme2-cta-border: var(--border-width) solid var(--color-neutral-white);
        --scheme2-cta-hover-text: var(--color-neutral-darkest);
        --scheme2-cta-hover-background: var(--color-neutral-white);
        --scheme2-cta-hover-border: var(--border-width) solid var(--color-neutral-white);
        --scheme2-cta-active-text: var(--color-neutral-darkest);
        --scheme2-cta-active-background: var(--color-neutral-lighter);
        --scheme2-cta-active-border: var(--border-width) solid var(--color-neutral-lighter);
        --scheme2-cta-shadow: none;
        --scheme2-cta-hover-shadow: none;
        --scheme2-focus-outline: var(--color-neutral-white);
        --scheme2-active-text: var(--color-neutral-darkest);
        --scheme2-active-background: var(--color-neutral-white);
        --scheme2-active-border: var(--border-width) solid var(--color-neutral-darkest);
        --scheme2-input-background: transparent;
        --scheme2-input-border: var(--border-width) solid var(--opacity-color-white-20);
        --scheme2-input-color: var(--color-neutral-white);
        --scheme2-input-placeholder: var(--opacity-color-white-60);
        --scheme2-input-focus-border: var(--color-neutral-white);
        --scheme2-input-focus-shadow: 0 0 0 3px var(--opacity-color-white-15);
        --scheme2-input-autofill-background: var(--opacity-color-white-10);
        --scheme2-input-autofill-color: var(--color-neutral-white);
        
       
    }

    /* =========================
       2. Responsive Variables
    ========================= */
    ${QUERIES.maxTablet} {
        :root {
            --heading1-size: var(--heading1-size-tablet);
            --heading2-size: var(--heading2-size-tablet);
            --heading3-size: var(--heading3-size-tablet);
            --heading4-size: var(--heading4-size-tablet);
            --heading5-size: var(--heading5-size-tablet);
            --heading6-size: var(--heading6-size-tablet);
            --tagline-size: var(--tagline-size-tablet);

            --body-large-size: var(--body-large-size-tablet);
            --body-base-size: var(--body-base-size-tablet);
            --body-small-size: var(--body-small-size-tablet);
            --body-tiny-size: var(--body-tiny-size-tablet);

            --spacing-xsmall: 0.25rem;
            --spacing-small: 0.45rem;
            --spacing-base: 0.8rem;
            --spacing-large: 1.25rem;
            --spacing-xlarge: 1.7rem;

            --width-section: 90vw;
            --width-logo: 70px;
            --width-logo-small: 40px;
            
            --button-padding-v: 0.65rem;
            --button-padding-h: 1.25rem;
            --button-icon-only-size: 2.25rem;
            --button-icon-size: 1.125rem;
        }
    }

    ${QUERIES.maxMobile} {
        :root {
            --heading1-size: var(--heading1-size-mobile);
            --heading2-size: var(--heading2-size-mobile);
            --heading3-size: var(--heading3-size-mobile);
            --heading4-size: var(--heading4-size-mobile);
            --heading5-size: var(--heading5-size-mobile);
            --heading6-size: var(--heading6-size-mobile);
            --tagline-size: var(--tagline-size-mobile);

            --body-large-size: var(--body-large-size-mobile);
            --body-base-size: var(--body-base-size-mobile);
            --body-small-size: var(--body-small-size-mobile);
            --body-tiny-size: var(--body-tiny-size-mobile);
            --body-line: 1.5;

            --spacing-xsmall: 0.2rem;
            --spacing-small: 0.4rem;
            --spacing-base: 0.6rem;
            --spacing-large: 1rem;
            --spacing-xlarge: 1.4rem;

            --width-section: 95vw;
            --width-logo: 60px;
            --width-logo-small: 30px;

            --button-padding-v: 0.5rem;
            --button-padding-h: 1rem;
            --button-icon-only-size: 2rem;
            --button-icon-size: 1rem;

            /* Mobile Padding Overrides */
            --padding-global: var(--padding-global-mobile);
            --padding-section-large: var(--padding-section-large-mobile);
            --padding-section-medium: var(--padding-section-medium-mobile);
            --padding-section-small: var(--padding-section-small-mobile);
        }
    }

    /* =========================
       3. Reset & Base Styles
    ========================= */
    *, *::before, *::after {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
    }

    /* =========================
       4. Typography
    ========================= */
    html, body {
        color: var(--scheme1-text);
        background-color: var(--scheme1-background);
        font-family: var(--font-family);
        font-size: var(--body-base-size);
        line-height: var(--body-line);
        height: 100%;
    }

    h1, h2, h3, h4, h5, h6 {
        font-family: var(--font-family-emphasis);
        font-weight: var(--font-weight-regular);
        line-height: 1.2;
        color: var(--scheme1-text);
        margin: var(--spacing-base) 0;
        text-transform: uppercase;
    }

    h1 {
        font-size: var(--heading1-size);
        line-height: var(--heading1-line);
        font-weight: var(--font-weight-bold);
    }

    h2 {
        font-size: var(--heading2-size);
        line-height: var(--heading2-line);
    }

    h3 {
        font-size: var(--heading3-size);
        line-height: var(--heading3-line);
    }

    h4 {
        font-size: var(--heading4-size);
        line-height: var(--heading4-line);
    }

    h5 {
        font-size: var(--heading5-size);
        line-height: var(--heading5-line);
    }

    h6 {
        font-size: var(--heading6-size);
        line-height: var(--heading6-line);
    }

    body, p {
        font-family: var(--font-family);
        font-size: var(--body-base-size);
        line-height: var(--body-line);
        color: var(--scheme1-text);
    }

    p {
        opacity: 1;
    }

    mark {
        background: var(--color-neutral-light); 
    }

    a {
        color: var(--scheme1-accent);
        cursor: pointer;
        text-decoration: none;
        transition: color 0.2s;
        &:hover {
            color: var(--scheme1-hover-background);
        }
    }

    label {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }

    /* =========================
       5. Layout & Containers
    ========================= */
    section {
        padding: var(--spacing-xlarge) 0;
        width: 100%;
    }

    img {
        max-width: 100%;
        height: auto;
    }

    /* =========================
       6. Elements
    ========================= */
    /* --- Buttons --- */
    button {
        background: none;
        border: var(--border-transparent);
        border-radius: 0;
        color: inherit;
        cursor: pointer;
        font: inherit;
        padding: 0;
        transition: none;
    }
        
    /* Button Groups */
    .btn-group {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-base);
    }
    
    ${QUERIES.maxMobile} {
        .btn-group .btn {
            flex: 1;            /* Allow buttons to grow on mobile */
            text-align: center; /* Center text for better appearance when wrapped */
        }
    }

    /* Social Media Buttons */
    .btn--social {
        border-radius: var(--radius-circle);
        background: var(--color-neutral-darkest);
        color: var(--color-neutral-white);
        padding: var(--button-padding-icon);
        transition: transform 0.2s ease, background-color 0.2s ease;
    }

    .btn--social:hover {
        transform: var(--hover-scale);
    }

    .btn--social.btn--instagram:hover {
        background: var(--color-instagram);
    }

    /* --- Inputs --- */
    input, textarea, select {
        background-color: var(--color-neutral-white);
        border: var(--scheme1-input-border);
        border-width: 0 0 var(--border-width) 0;
        border-radius: var(--radius-input);
        height: var(--height-control);
        max-width: var(--width-control);
        padding: var(--ui-padding-medium) 0;
        transition: var(--transition-all-ease);
        font-family: var(--font-family);
        font-style: normal;
        font-weight: var(--font-weight-regular);
        font-size: var(--body-base-size);
        line-height: var(--body-line);
        
        &::placeholder {
            color: var(--scheme1-input-placeholder);
        }

        &:focus {
            border-color: var(--scheme1-input-focus-border);
            outline: none;
            box-shadow: none;
            transform: none;
        }
    }

    select {
        appearance: none;
        background-image: linear-gradient(45deg, transparent 50%, var(--opacity-color-black-60) 50%),
          linear-gradient(135deg, var(--opacity-color-black-60) 50%, transparent 50%);
        background-position:
          calc(100% - 18px) 50%,
          calc(100% - 12px) 50%;
        background-size:
          6px 6px,
          6px 6px;
        background-repeat: no-repeat;
        padding-right: var(--ui-padding-large);
    }

    .ui-tag {
        display: inline-flex;
        align-items: flex-start;
        padding: var(--ui-tag-padding-v) var(--ui-tag-padding-h);
        border: none;
        border-radius: var(--radius-tag);
        font-size: var(--ui-tag-font-size);
        font-weight: var(--font-weight-semibold);
        line-height: var(--body-line);
        color: var(--color-neutral-darkest);
    }

    .ui-toggle {
        display: inline-flex;
        align-items: center;
        gap: var(--ui-padding-large);
        font-size: var(--heading6-size);
        line-height: var(--body-line);
    }

    .ui-toggle__switch {
        width: var(--ui-toggle-size);
        height: var(--ui-toggle-height);
        padding: var(--ui-padding-small);
        border: none;
        border-radius: var(--radius-toggle);
        display: inline-flex;
        align-items: center;
    }

    .ui-toggle__switch::after {
        content: '';
        width: var(--ui-toggle-dot-size);
        height: var(--ui-toggle-dot-size);
        border-radius: var(--radius-circle);
        background: var(--color-neutral-darkest);
        display: block;
    }

    .ui-slider-arrow {
        width: var(--width-slider-arrow);
        height: var(--height-slider-arrow);
        padding: var(--ui-padding-large);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: var(--color-neutral-white);
        border: none;
        border-radius: var(--radius-xsmall);
    }

    /* --- Lists --- */
    ul, ol {
        list-style: none;
        padding: 0;
        text-decoration: none;
    }
    
    /* --- Scrollbar --- */
    * {
        scrollbar-width: thin;
        scrollbar-color: var(--color-neutral-dark) var(--color-neutral-lightest);
    }

    ::-webkit-scrollbar {
        width: 12px;
        height: 12px;
    }

    ::-webkit-scrollbar-track {
        background: var(--color-neutral-lightest);
        border-radius: var(--radius-xlarge);
    }

    ::-webkit-scrollbar-thumb {
        background: var(--color-neutral-dark);
        border: 3px solid var(--color-neutral-lightest);
        border-radius: var(--radius-xlarge);
    }

    ::-webkit-scrollbar-thumb:hover {
        background: var(--color-neutral-darkest);
    }

    ::-webkit-scrollbar-corner {
        background: transparent;
    }

    /* =========================
       7. Utility Classes
    ========================= */
    .hidden {
        display: none !important;
    }
    
    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }
    
    .scroll-lock {
        overflow: hidden;
        position: fixed;
        width: 100%;
    }

    /* =========================
       8. Vendor Specific
    ========================= */
    /* --- Gatsby --- */
    .gatsby-image-wrapper img[src*=base64\\] {
        image-rendering: -moz-crisp-edges;
        image-rendering: pixelated;
    }

    /* =========================
       9. Accessibility Enhancements
    ========================= */
    @media (prefers-reduced-motion: reduce) {
    *, ::before, ::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
    }
    
    .btn:hover, .btn:active {
        transform: none !important;
    }
}
`;

export default GlobalStyles;
